import { useState, useEffect, useRef } from 'react';

type Account = {
  contractNumber: string;
  parent?: string;
};

type AssignedAccountEntry = {
  parentAccountNumber?: string;
  accounts?: { accountNumber: string }[];
};

type AssignedAccountRecord = {
  grandfatherClientNumber?: string;
  assignedAccountIds?: AssignedAccountEntry[];
};

type DetailedUser = {
  assignedAccounts?: AssignedAccountRecord[];
};

type UseAccountSelectionTreeParams = {
  accounts: Account[];
  detailedUser: DetailedUser;
  accountsSize: number;
  assignedGrandfatherClientNumber: string;
};

/**
 * Pure function — no hook dependencies, so defined at module level to avoid
 * being recreated on every render of useAccountSelectionTree.
 */
function getInitialCheckboxSelection(obj: AssignedAccountRecord): string[] {
  const result: string[] = [];

  if (obj.grandfatherClientNumber) {
    result.push(obj.grandfatherClientNumber);
  }

  (obj.assignedAccountIds ?? []).forEach((account) => {
    if (account.parentAccountNumber) {
      result.push(account.parentAccountNumber);
    }
    (account.accounts ?? []).forEach((subAccount) => {
      if (subAccount.accountNumber) {
        result.push(subAccount.accountNumber);
      }
    });
  });

  return result;
}

/**
 * Pure functions — no hook dependencies, defined at module level.
 * Taking `accounts` as an explicit parameter makes them independently testable
 * and avoids closure-based recreation on every hook render.
 */
function addAccountWithParents(accounts: Account[], contractNumber: string, current: string[]): string[] {
  const account = accounts.find((item) => item.contractNumber === contractNumber);
  if (!account) return current;

  const result = [...current, contractNumber];
  if (account.parent) {
    const parentExists = accounts.some((item) => item.contractNumber === account.parent);
    if (parentExists && !result.includes(account.parent)) {
      return addAccountWithParents(accounts, account.parent, result);
    }
  }
  return result;
}

function removeAccountWithChildren(accounts: Account[], contractNumber: string, current: string[]): string[] {
  const account = accounts.find((item) => item.contractNumber === contractNumber);
  if (!account) return current;

  let result = current.filter((item) => item !== contractNumber);
  const childAccounts = accounts.filter((item) => item.parent === contractNumber);
  childAccounts.forEach((child) => {
    result = removeAccountWithChildren(accounts, child.contractNumber, result);
  });
  return result;
}

/**
 * useAccountSelectionTree
 *
 * Manages hierarchical account checkbox selection for the CustomerAssignUserAccounts component.
 * Handles tree-walk selection logic: selecting a node selects all parent nodes; deselecting
 * a node deselects all child nodes.
 *
 * Also builds the initial selection state from the loaded detailedUser once the account list
 * is available.
 *
 * @returns selectedAccounts, initialSelectedAccounts, selectedAccountsRef (stable ref for submit),
 *          and handleCheckboxSelection event handler.
 */
export const useAccountSelectionTree = ({
  accounts,
  detailedUser,
  accountsSize,
  assignedGrandfatherClientNumber,
}: UseAccountSelectionTreeParams) => {
  const [selectedAccounts, setSelectedAccounts] = useState<string[]>([]);
  const [initialSelectedAccounts, setInitialSelectedAccounts] = useState<string[]>([]);

  // Stable ref so handleSubmit in the parent always reads the latest selected accounts
  // without needing selectedAccounts in the submit button's onClick closure.
  const selectedAccountsRef = useRef<string[]>(selectedAccounts);
  useEffect(() => {
    selectedAccountsRef.current = selectedAccounts;
  }, [selectedAccounts]);

  // Once user details and account list are both loaded, build the initial selection.
  // detailedUser and accounts are used as signals; assignedGrandfatherClientNumber is
  // used inside but is a stable string, so excluded to avoid spurious re-runs.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (Object.keys(detailedUser).length > 0 && accountsSize > 0) {
      const accountAssignmentDetails = (detailedUser.assignedAccounts ?? []).find(
        (obj) => obj.grandfatherClientNumber === assignedGrandfatherClientNumber
      );
      if (accountAssignmentDetails) {
        const initSelectedAccounts = getInitialCheckboxSelection(accountAssignmentDetails);
        setSelectedAccounts(initSelectedAccounts);
        setInitialSelectedAccounts(initSelectedAccounts);
      }
    }
  }, [detailedUser, accounts]);

  const handleCheckboxSelection = ({ contractNumber }: { contractNumber: string }) => {
    setSelectedAccounts((prev) =>
      prev.includes(contractNumber)
        ? removeAccountWithChildren(accounts, contractNumber, prev)
        : addAccountWithParents(accounts, contractNumber, prev)
    );
  };

  return {
    selectedAccounts,
    initialSelectedAccounts,
    selectedAccountsRef,
    handleCheckboxSelection,
  };
};
